import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  BarChart3,
  TrendingUp,
  Users,
  MessageSquare,
  Heart,
  Award,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { useSocialOverview, usePostTrend, useTopPosters } from '../../hooks/useSocialAnalytics';

export default function SocialAnalytics() {
  const { data: stats, isLoading: statsLoading } = useSocialOverview();
  const { data: postTrend, isLoading: trendLoading } = usePostTrend();
  const { data: topPosters, isLoading: postersLoading } = useTopPosters(10);

  const maxTrendCount = Math.max(...(postTrend?.map((m) => m.count) || [1]));

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8 flex items-center gap-3">
        <Link
          to="/social"
          className="p-2 rounded-lg text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#363232] transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#9B1421]/20 flex items-center justify-center">
            <BarChart3 size={20} className="text-text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#EBFFEE]">Social Analytics</h1>
            <p className="text-sm text-[#A9A3A3]">Track community engagement and growth</p>
          </div>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Posts', value: stats?.totalPosts ?? '-', icon: MessageSquare, color: 'text-blue-400' },
          { label: 'Total Comments', value: stats?.totalComments ?? '-', icon: MessageSquare, color: 'text-green-400' },
          { label: 'Total Reactions', value: stats?.totalReactions ?? '-', icon: Heart, color: 'text-pink-400' },
          { label: 'Active Posters', value: stats?.activePosters ?? '-', icon: Users, color: 'text-purple-400' },
        ].map((stat) => (
          <Card key={stat.label} className="bg-[#221F1F] border-[#504A4A]">
            <CardContent className="!p-4">
              <div className="flex items-center gap-3">
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
                <div>
                  <p className="text-2xl font-bold text-[#EBFFEE]">
                    {statsLoading ? '...' : stat.value}
                  </p>
                  <p className="text-xs text-[#A9A3A3]">{stat.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Post Trend Chart */}
        <Card className="bg-[#221F1F] border-[#504A4A]">
          <CardHeader>
            <CardTitle className="text-[#EBFFEE] text-sm flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-rollops-accent" />
              Post Activity (Last 6 Months)
            </CardTitle>
          </CardHeader>
          <CardContent className="!p-4">
            {trendLoading ? (
              <div className="h-48 flex items-center justify-center text-[#A9A3A3]">Loading...</div>
            ) : !postTrend?.length ? (
              <div className="h-48 flex items-center justify-center text-[#A9A3A3]">No data yet</div>
            ) : (
              <div className="space-y-3">
                {postTrend.map((month) => (
                  <div key={month.month} className="flex items-center gap-3">
                    <span className="text-xs text-[#A9A3A3] w-20 text-right flex-shrink-0">
                      {month.month}
                    </span>
                    <div className="flex-1 h-6 bg-[#363232] rounded-lg overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#9B1421] to-[#E8535E] rounded-lg transition-all duration-500 flex items-center justify-end pr-2"
                        style={{
                          width: `${maxTrendCount > 0 ? Math.max((month.count / maxTrendCount) * 100, 2) : 2}%`,
                        }}
                      >
                        {month.count > 0 && (
                          <span className="text-xs text-white font-medium">{month.count}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Posters */}
        <Card className="bg-[#221F1F] border-[#504A4A]">
          <CardHeader>
            <CardTitle className="text-[#EBFFEE] text-sm flex items-center gap-2">
              <Award className="w-4 h-4 text-rollops-accent" />
              Most Active Members
            </CardTitle>
          </CardHeader>
          <CardContent className="!p-0">
            {postersLoading ? (
              <div className="p-4 text-center text-[#A9A3A3]">Loading...</div>
            ) : !topPosters?.length ? (
              <div className="p-4 text-center text-[#A9A3A3]">No activity yet</div>
            ) : (
              <div className="divide-y divide-[#504A4A]">
                {topPosters.map((poster, index) => (
                  <div key={poster.user_id} className="flex items-center gap-3 p-3">
                    <span className={`text-sm font-bold w-6 text-center ${
                      index === 0 ? 'text-yellow-400' :
                      index === 1 ? 'text-gray-300' :
                      index === 2 ? 'text-orange-400' :
                      'text-[#706A6A]'
                    }`}>
                      {index + 1}
                    </span>
                    <div className="w-8 h-8 rounded-full bg-[#363232] flex items-center justify-center overflow-hidden flex-shrink-0">
                      {poster.avatar_url ? (
                        <img src={poster.avatar_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Users className="w-4 h-4 text-[#706A6A]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#EBFFEE] truncate">
                        {poster.first_name} {poster.last_name}
                      </p>
                    </div>
                    <span className="text-sm text-[#A9A3A3]">
                      {poster.post_count} post{poster.post_count !== 1 ? 's' : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Engagement Insights */}
        <Card className="bg-[#221F1F] border-[#504A4A] lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-[#EBFFEE] text-sm flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-rollops-accent" />
              Community Health
            </CardTitle>
          </CardHeader>
          <CardContent className="!p-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-[#363232] border border-[#504A4A]">
                <p className="text-xs text-[#A9A3A3] mb-1">Avg Comments / Post</p>
                <p className="text-2xl font-bold text-[#EBFFEE]">
                  {statsLoading
                    ? '...'
                    : stats && stats.totalPosts > 0
                      ? (stats.totalComments / stats.totalPosts).toFixed(1)
                      : '0'}
                </p>
                <p className="text-xs text-[#706A6A] mt-1">
                  Higher = more engaged community
                </p>
              </div>
              <div className="p-4 rounded-lg bg-[#363232] border border-[#504A4A]">
                <p className="text-xs text-[#A9A3A3] mb-1">Avg Reactions / Post</p>
                <p className="text-2xl font-bold text-[#EBFFEE]">
                  {statsLoading
                    ? '...'
                    : stats && stats.totalPosts > 0
                      ? (stats.totalReactions / stats.totalPosts).toFixed(1)
                      : '0'}
                </p>
                <p className="text-xs text-[#706A6A] mt-1">
                  Reactions show content resonance
                </p>
              </div>
              <div className="p-4 rounded-lg bg-[#363232] border border-[#504A4A]">
                <p className="text-xs text-[#A9A3A3] mb-1">Report Rate</p>
                <p className="text-2xl font-bold text-[#EBFFEE]">
                  {statsLoading
                    ? '...'
                    : stats && stats.totalPosts > 0
                      ? `${((stats.pendingReports / stats.totalPosts) * 100).toFixed(1)}%`
                      : '0%'}
                </p>
                <p className="text-xs text-[#706A6A] mt-1">
                  Lower = healthier community
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
